// Booth Rental Settings Tab
import React, { useState } from "react";
import SettingsSaveButtons from "./SettingsSaveButtons";

const BoothRentalSettings = ({
  settings,
  updateSettings,
  saveSettings,
  saving,
  hasUnsavedChanges,
  resetSettings,
}) => {
  // Get booth rental settings with defaults
  const boothSettings = settings?.boothRenterSettings || {
    enabled: false,
    subscriptionMonthly: 10,
    businessMonthlyFeePerRenter: null,
    stripeR7MonthlyPriceId: "",
    rentPayment: {
      applicationFeePercent: 0,
      minRentAmount: 5000,
      maxRentAmount: 200000,
      allowedBillingCycles: ["weekly", "biweekly", "monthly"],
      gracePeriodDays: 3,
      maxFailedAttempts: 3,
    },
    offerDefaults: {
      maxSeatsPerOffer: 10,
      offerExpirationDays: 90,
      referralCodeLength: 12,
    },
    defaultShowOnSalonPage: true,
    r7CanSellProducts: true,
    r7ProductFeePercent: 9,
    maxBoothRentalsPerR7: 5,
    trackBookingSource: true,
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveSettings();
  };

  const handleUpdate = (field, value) => {
    updateSettings({
      boothRenterSettings: {
        ...boothSettings,
        [field]: value,
      },
    });
  };

  const handleNestedUpdate = (parent, field, value) => {
    updateSettings({
      boothRenterSettings: {
        ...boothSettings,
        [parent]: {
          ...boothSettings[parent],
          [field]: value,
        },
      },
    });
  };

  const handleBillingCyclesChange = (cycle, checked) => {
    const cycles = boothSettings.rentPayment.allowedBillingCycles || [];
    const newCycles = checked
      ? [...cycles, cycle]
      : cycles.filter((c) => c !== cycle);

    updateSettings({
      boothRenterSettings: {
        ...boothSettings,
        rentPayment: {
          ...boothSettings.rentPayment,
          allowedBillingCycles: newCycles,
        },
      },
    });
  };

  // ── Payment Recovery Tool ──────────────────────────────────────────────────
  const [recovering, setRecovering] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState(null);

  const handleRecoverPayments = async () => {
    if (!window.confirm("Scan for stuck pending booth rental payments and recover them? This is safe to run multiple times.")) return;
    setRecovering(true);
    setRecoveryResult(null);
    try {
      const token = localStorage.getItem("ogestyle_token");
      const apiUrl = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
      const res = await fetch(`${apiUrl}/api/booth-rental-payments/admin/recover-pending`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setRecoveryResult(data);
    } catch (err) {
      setRecoveryResult({ success: false, error: err.message });
    } finally {
      setRecovering(false);
    }
  };
  // ── Visibility Recalculation Tool ─────────────────────────────────────────
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);

  const handleRecalcVisibility = async () => {
    if (!window.confirm("Recompute isPubliclyVisible for all R7 booth renters? Run this after deploying visibility gate changes.")) return;
    setRecalculating(true);
    setRecalcResult(null);
    try {
      const token = localStorage.getItem("ogestyle_token");
      const apiUrl = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
      const res = await fetch(`${apiUrl}/api/booth-rental-payments/admin/recalculate-visibility`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setRecalcResult(data);
    } catch (err) {
      setRecalcResult({ success: false, error: err.message });
    } finally {
      setRecalculating(false);
    }
  };
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <form onSubmit={handleSubmit}>
      {/* ── Visibility Recalculation Tool ─────────────────────────────────── */}
      <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "12px", padding: "24px", marginBottom: "24px" }}>
        <h2 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "6px", color: "#15803d" }}>
          👁️ Recalculate R7 Visibility
        </h2>
        <p style={{ color: "#166534", fontSize: "13px", marginBottom: "16px" }}>
          Recomputes the <strong>isPubliclyVisible</strong> flag for every booth renter using the full gate checklist (live, active, services, schedule, photo, professional types, Stripe, rent paid, parent shop). Run this once after deploying visibility changes.
        </p>
        <button
          type="button"
          onClick={handleRecalcVisibility}
          disabled={recalculating}
          style={{
            background: recalculating ? "#d1d5db" : "#16a34a",
            color: "white",
            border: "none",
            borderRadius: "8px",
            padding: "10px 20px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: recalculating ? "not-allowed" : "pointer",
          }}
        >
          {recalculating ? "Recalculating…" : "🔄 Recalculate Visibility"}
        </button>
        {recalcResult && (
          <div style={{ marginTop: "16px", background: "white", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "16px", fontSize: "13px" }}>
            {recalcResult.success === false ? (
              <p style={{ color: "#dc2626" }}>❌ Error: {recalcResult.error}</p>
            ) : (
              <>
                <p style={{ fontWeight: "600", marginBottom: "8px" }}>
                  {recalcResult.visible} of {recalcResult.total} booth renters now publicly visible
                </p>
                {(recalcResult.results || []).filter(r => !r.isVisible).map((r, i) => (
                  <div key={i} style={{ borderTop: "1px solid #f3f4f6", paddingTop: "8px", marginTop: "8px", color: "#6b7280" }}>
                    ⚠️ {r.staffId}: {(r.failedChecks || [r.error]).join(", ")}
                  </div>
                ))}
                {recalcResult.visible === recalcResult.total && recalcResult.total > 0 && (
                  <p style={{ color: "#16a34a" }}>✅ All booth renters are publicly visible.</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {/* ────────────────────────────────────────────────────────────────────── */}
      <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "12px", padding: "24px", marginBottom: "24px" }}>
        <h2 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "6px", color: "#9a3412" }}>
          🔧 Recover Stuck Payments
        </h2>
        <p style={{ color: "#7c2d12", fontSize: "13px", marginBottom: "16px" }}>
          If a booth renter completed Stripe checkout but their dashboard still shows "Due Today" or their payment history shows "Pending", use this to scan and fix stuck payments.
          This recovery verifies Stripe reports the checkout session as paid before any local status updates are applied.
        </p>
        <button
          type="button"
          onClick={handleRecoverPayments}
          disabled={recovering}
          style={{
            background: recovering ? "#d1d5db" : "#ea580c",
            color: "white",
            border: "none",
            borderRadius: "8px",
            padding: "10px 20px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: recovering ? "not-allowed" : "pointer",
          }}
        >
          {recovering ? "Scanning…" : "🔍 Recover Stuck Payments"}
        </button>

        {recoveryResult && (
          <div style={{ marginTop: "16px", background: "white", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "16px", fontSize: "13px" }}>
            {recoveryResult.success === false ? (
              <p style={{ color: "#dc2626" }}>❌ Error: {recoveryResult.error}</p>
            ) : (
              <>
                <p style={{ fontWeight: "600", marginBottom: "8px" }}>
                  Processed {recoveryResult.processed} payment(s)
                </p>
                {(recoveryResult.results || []).map((r, i) => (
                  <div key={i} style={{ borderTop: "1px solid #f3f4f6", paddingTop: "8px", marginTop: "8px" }}>
                    <span style={{ color: r.outcome === "recovered" ? "#16a34a" : "#6b7280" }}>
                      {r.outcome === "recovered" ? "✅" : "⏭️"} Payment ${((r.amount || 0) / 100).toFixed(2)} — {r.outcome}
                      {r.outcome === "recovered" && r.nextRentDueDate
                        ? ` → next due ${new Date(r.nextRentDueDate).toLocaleDateString()}`
                        : ""}
                    </span>
                  </div>
                ))}
                {recoveryResult.processed === 0 && (
                  <p style={{ color: "#6b7280" }}>No stuck payments found. All booth rental payments are up to date.</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {/* ────────────────────────────────────────────────────────────────────── */}

      {/* Feature Enablement Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          🏪 Booth Rental Feature
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Enable or disable the booth rental feature platform-wide. This allows
          R1 salon owners to rent booth space to independent professionals (R7).
        </p>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          <input
            type="checkbox"
            checked={boothSettings.enabled}
            onChange={(e) => handleUpdate("enabled", e.target.checked)}
            style={{
              width: "18px",
              height: "18px",
              marginRight: "12px",
              cursor: "pointer",
            }}
          />
          <span style={{ fontWeight: "500" }}>
            Enable Booth Rental Feature Platform-Wide
          </span>
        </label>

        {!boothSettings.enabled && (
          <div
            style={{
              marginTop: "16px",
              padding: "12px",
              background: "#fef3c7",
              border: "1px solid #fbbf24",
              borderRadius: "6px",
              fontSize: "13px",
              color: "#92400e",
            }}
          >
            ⚠️ Feature is currently disabled. Enable it to allow booth rentals
            platform-wide.
          </div>
        )}
      </div>

      {/* R7 Subscription Settings */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          💳 R7 Subscription Pricing
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Monthly subscription fee for booth renters (R7). Only monthly billing
          is available.
        </p>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Monthly Subscription ($)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={boothSettings.subscriptionMonthly}
            onChange={(e) =>
              handleUpdate("subscriptionMonthly", parseFloat(e.target.value))
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Monthly subscription fee for R7 booth renters
          </p>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Stripe Monthly Price ID
          </label>
          <input
            type="text"
            value={boothSettings.stripeR7MonthlyPriceId || ""}
            onChange={(e) =>
              handleUpdate("stripeR7MonthlyPriceId", e.target.value)
            }
            placeholder="price_xxxxxxxxxxxxx"
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Stripe price ID for monthly R7 subscriptions (run
            setup-r7-stripe-prices.js)
          </p>
        </div>
      </div>

      {/* Rent Payment Settings */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          💰 Rent Payment Configuration
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Configure rent payment limits and rules. R1 salon owners set the
          actual rent amount when creating offers.
        </p>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Platform Fee (%)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="20"
            value={boothSettings.rentPayment.platformFeePercent || 0}
            onChange={(e) =>
              handleNestedUpdate(
                "rentPayment",
                "platformFeePercent",
                parseFloat(e.target.value),
              )
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Platform fee taken from each rent payment (max 20%, charged to R1
            business owner)
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Minimum Rent Amount ($)
            </label>
            <input
              type="number"
              step="1"
              min="0"
              value={boothSettings.rentPayment.minRentAmount / 100}
              onChange={(e) =>
                handleNestedUpdate(
                  "rentPayment",
                  "minRentAmount",
                  parseInt(e.target.value) * 100,
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
              required
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              Minimum rent amount per billing cycle
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Maximum Rent Amount ($)
            </label>
            <input
              type="number"
              step="1"
              min="0"
              value={boothSettings.rentPayment.maxRentAmount / 100}
              onChange={(e) =>
                handleNestedUpdate(
                  "rentPayment",
                  "maxRentAmount",
                  parseInt(e.target.value) * 100,
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
              required
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              Maximum rent amount per billing cycle
            </p>
          </div>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{
              display: "block",
              fontWeight: "500",
              marginBottom: "12px",
            }}
          >
            Allowed Billing Cycles
          </label>
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            {["weekly", "biweekly", "monthly"].map((cycle) => (
              <label
                key={cycle}
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    boothSettings.rentPayment.allowedBillingCycles?.includes(
                      cycle,
                    ) || false
                  }
                  onChange={(e) =>
                    handleBillingCyclesChange(cycle, e.target.checked)
                  }
                  style={{
                    width: "16px",
                    height: "16px",
                    marginRight: "8px",
                    cursor: "pointer",
                  }}
                />
                <span style={{ fontSize: "14px", textTransform: "capitalize" }}>
                  {cycle}
                </span>
              </label>
            ))}
          </div>
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Billing cycle options available to salon owners when creating offers
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Grace Period (Days)
            </label>
            <input
              type="number"
              step="1"
              min="0"
              max="30"
              value={boothSettings.rentPayment.gracePeriodDays}
              onChange={(e) =>
                handleNestedUpdate(
                  "rentPayment",
                  "gracePeriodDays",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
              required
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              Days before failed payment triggers termination
            </p>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Max Failed Attempts
            </label>
            <input
              type="number"
              step="1"
              min="1"
              max="10"
              value={boothSettings.rentPayment.maxFailedAttempts}
              onChange={(e) =>
                handleNestedUpdate(
                  "rentPayment",
                  "maxFailedAttempts",
                  parseInt(e.target.value),
                )
              }
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
              required
            />
            <p
              style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}
            >
              Max payment failures before termination
            </p>
          </div>
        </div>
      </div>

      {/* Offer Defaults */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          🎫 Booth Rental Offer Defaults
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Default settings for booth rental offers created by R1 salon owners.
        </p>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Max Seats Per Offer
          </label>
          <input
            type="number"
            step="1"
            min="1"
            max="50"
            value={boothSettings.offerDefaults.maxSeatsPerOffer}
            onChange={(e) =>
              handleNestedUpdate(
                "offerDefaults",
                "maxSeatsPerOffer",
                parseInt(e.target.value),
              )
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Maximum number of booth spaces per rental offer
          </p>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Offer Expiration (Days)
          </label>
          <input
            type="number"
            step="1"
            min="1"
            max="365"
            value={boothSettings.offerDefaults.offerExpirationDays}
            onChange={(e) =>
              handleNestedUpdate(
                "offerDefaults",
                "offerExpirationDays",
                parseInt(e.target.value),
              )
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Days until offer expires and can no longer accept signups
          </p>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Referral Code Length
          </label>
          <input
            type="number"
            step="1"
            min="6"
            max="32"
            value={boothSettings.offerDefaults.referralCodeLength}
            onChange={(e) =>
              handleNestedUpdate(
                "offerDefaults",
                "referralCodeLength",
                parseInt(e.target.value),
              )
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Length of auto-generated referral codes for booth rental offers
          </p>
        </div>
      </div>

      {/* R7 Features */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          🎨 R7 Booth Renter Features
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Configure capabilities and limitations for booth renters (R7).
        </p>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              fontSize: "14px",
              marginBottom: "16px",
            }}
          >
            <input
              type="checkbox"
              checked={boothSettings.defaultShowOnSalonPage}
              onChange={(e) =>
                handleUpdate("defaultShowOnSalonPage", e.target.checked)
              }
              style={{
                width: "18px",
                height: "18px",
                marginRight: "12px",
                cursor: "pointer",
              }}
            />
            <span>
              <strong>Show R7 on Salon Page by Default</strong>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                }}
              >
                Default visibility setting for new booth rentals (R1 owners can
                override per offer)
              </p>
            </span>
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              fontSize: "14px",
              marginBottom: "16px",
            }}
          >
            <input
              type="checkbox"
              checked={boothSettings.r7CanSellProducts}
              onChange={(e) =>
                handleUpdate("r7CanSellProducts", e.target.checked)
              }
              style={{
                width: "18px",
                height: "18px",
                marginRight: "12px",
                cursor: "pointer",
              }}
            />
            <span>
              <strong>Allow R7 to Sell Products</strong>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                }}
              >
                Enable booth renters to sell products through the platform
              </p>
            </span>
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            <input
              type="checkbox"
              checked={boothSettings.trackBookingSource}
              onChange={(e) =>
                handleUpdate("trackBookingSource", e.target.checked)
              }
              style={{
                width: "18px",
                height: "18px",
                marginRight: "12px",
                cursor: "pointer",
              }}
            />
            <span>
              <strong>Track Booking Source</strong>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                }}
              >
                Track whether bookings came from salon page or R7's direct link
              </p>
            </span>
          </label>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Max Booth Rentals Per R7
          </label>
          <input
            type="number"
            step="1"
            min="1"
            max="50"
            value={boothSettings.maxBoothRentalsPerR7}
            onChange={(e) =>
              handleUpdate("maxBoothRentalsPerR7", parseInt(e.target.value))
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Maximum number of booth rentals a single R7 can maintain
            simultaneously
          </p>
        </div>
      </div>

      {/* Save Buttons */}
      <SettingsSaveButtons
        onSave={handleSubmit}
        onCancel={resetSettings}
        saving={saving}
        hasChanges={hasUnsavedChanges}
      />
    </form>
  );
};

export default BoothRentalSettings;
